// Gula de Agua business vocabulary. Values are stored in the database;
// labels are what the team sees (Spanish, the team's working language).

export type Option = { value: string; label: string };

export const CLIENT_STAGES: Option[] = [
  { value: "new", label: "Nuevo" },
  { value: "no_answer", label: "No contesta" },
  { value: "first_contact", label: "1er contacto" },
  { value: "second_contact", label: "2º contacto" },
  { value: "recontact", label: "Recontactar" },
  // Agreed to buy: only the payment / order is missing
  { value: "sure_purchase", label: "Compra segura" },
  { value: "no_purchase", label: "No compra" },
  { value: "purchased", label: "Compra" },
  // Out of the setters' board: the admins (or an agent) try to wake them up
  { value: "reactivation", label: "Reactivar" },
];

export const ORDER_STAGES: Option[] = [
  { value: "to_ship", label: "Pendiente de envío" },
  { value: "shipped", label: "Enviado" },
  { value: "received", label: "Recibido" },
  { value: "installer_notified", label: "Instalación solicitada" },
  { value: "installed", label: "Instalado" },
];

export const PRODUCTS: (Option & { price: number })[] = [
  { value: "blua_auxiliary_tap", label: "BLUA grifo auxiliar", price: 499 },
  { value: "blua_three_way_tap", label: "BLUA grifo 3 vías", price: 699 },
];

export const PLANS: Option[] = [
  { value: "none", label: "Sin mantenimiento" },
  { value: "maintenance_monthly", label: "Mantenimiento mensual" },
  { value: "maintenance_annual", label: "Mantenimiento anual" },
];

/** Stripe subscription statuses, as the team reads them. */
export const SUBSCRIPTION_STATUSES: (Option & {
  tone: "ok" | "warn" | "off";
})[] = [
  { value: "active", label: "Activo", tone: "ok" },
  { value: "trialing", label: "En prueba", tone: "ok" },
  { value: "past_due", label: "Impago", tone: "warn" },
  { value: "unpaid", label: "Impagado", tone: "warn" },
  { value: "incomplete", label: "Pago incompleto", tone: "warn" },
  { value: "paused", label: "Pausado", tone: "off" },
  { value: "canceled", label: "Cancelado", tone: "off" },
  { value: "incomplete_expired", label: "Caducado", tone: "off" },
];

/** What a Stripe subscription is for. */
export const SUBSCRIPTION_KINDS: Option[] = [
  { value: "maintenance", label: "Mantenimiento" },
  { value: "rental", label: "Alquiler" },
  { value: "installments", label: "Pago a plazos" },
  { value: "other", label: "Otro" },
];

export const PAYMENT_STATUSES: Option[] = [
  { value: "paid", label: "Pagado" },
  { value: "pending", label: "Pendiente" },
  { value: "refunded", label: "Devuelto" },
];

export const PAYMENT_METHODS: Option[] = [
  { value: "card", label: "Pago completo (tarjeta)" },
  { value: "klarna", label: "Klarna 3 meses" },
  { value: "financing", label: "Financiera (seQura)" },
  { value: "transfer", label: "Transferencia" },
  { value: "cash", label: "Efectivo (solo Cataluña)" },
  { value: "stripe_installments", label: "3 cuotas (Stripe)" },
  { value: "rental", label: "Alquiler" },
];

export const CALL_SLOTS: Option[] = [
  { value: "9-14", label: "9:00 – 14:00" },
  { value: "14-18", label: "14:00 – 18:00" },
  { value: "18-21", label: "18:00 – 21:00" },
];

export const LEAD_TEMPERATURES: (Option & { className: string })[] = [
  { value: "hot", label: "Caliente", className: "bg-red-100 text-red-800" },
  {
    value: "warm",
    label: "Templado",
    className: "bg-amber-100 text-amber-800",
  },
  { value: "cold", label: "Frío", className: "bg-sky-100 text-sky-800" },
];

/** Hot leads first, then warm, cold and unscored. */
export const temperatureRank = (value?: string | null) => {
  const index = LEAD_TEMPERATURES.findIndex((option) => option.value === value);
  return index === -1 ? LEAD_TEMPERATURES.length : index;
};

export const LOST_REASONS: Option[] = [
  { value: "price", label: "Precio" },
  { value: "not_understood", label: "No entiende el producto" },
  { value: "prefers_rental", label: "Prefiere alquiler" },
  { value: "already_has", label: "Ya tiene equipo" },
  { value: "no_coverage", label: "Sin cobertura" },
  { value: "not_installable", label: "No se puede instalar" },
  { value: "no_answer", label: "No contesta" },
  { value: "other", label: "Otro" },
];

const labelOfOne = (options: Option[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

/** Label of a value; comma separated values (multi-choice answers) are labelled one by one. */
export const labelOf = (options: Option[], value?: string | null) =>
  value
    ? value
        .split(",")
        .map((part) => labelOfOne(options, part.trim()))
        .join(" · ")
    : "";

export const formatEuro = (amount?: number | null) =>
  amount == null
    ? ""
    : amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" });

export const formatLongDate = (date?: string | null) =>
  date
    ? new Date(date).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

export const formatDate = (date?: string | null) =>
  date
    ? new Date(date).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
      })
    : "";

export const daysSince = (date?: string | null) =>
  date
    ? Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 864e5))
    : 0;
